import { useCallback, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Link } from '@mui/material';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ExtraUserDetails } from '../../../lib/models/auth/types';
import { ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import {
  PasswordValidationResult,
  PasswordValidationResultCleared,
} from '../../../lib/util/ValidationSchemes/PasswordSchema';
import {
  getEmailValidity,
  getFirstNameValidity,
  getLastNameValidity,
  validate,
  validateControl,
} from '../../../lib/util/ValidationSchemes/Validate';
import { ValidationType } from '../../../lib/util/ValidationSchemes/ValidationTypes';

import InputErrorLogo from '../../../assets/img/input-error-icon.svg';
import { InputControl, PasswordControl } from '../../../components';
import CheckboxControlV2 from '../../../components/CheckboxControl/CheckboxControlV2';
import config from '../../../config';
import {
  GTM_CATEGORIES,
  GTM_CLIENT_CREATION_STEPS,
  GTM_EVENTS,
  GTM_FORM_NAMES,
} from '../../../constants/gtm';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import {
  DefaultSignUpFormWithPoliciesErrors,
  SignUpFormWithPoliciesErrors,
} from '../../../store-util/EnrollmentState.constants';
import { hashSHA256 } from '../../../util/hash';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollAuthInfo.module.scss';

type Props = ReduxProps;
const ADDITIONAL_HEIGHT_PER_ERROR = 24;

const EnrollAuthInfo = (props: Props): JSX.Element => {
  const { enrollRegister, isAppLoaded, requestState, userId } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const { form } = EnrollmentState.use();
  const [formErrors, setFormErrors] = useState<SignUpFormWithPoliciesErrors>(
    DefaultSignUpFormWithPoliciesErrors,
  );
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState<boolean>(false);
  const [passwordValidationResult, setPasswordValidationResult] = useState<PasswordValidationResult>(
    PasswordValidationResultCleared,
  );
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showError, setShowError] = useState(false);
  const planId = location.state?.id;
  const autoPass: boolean | undefined = location.state?.autoPass;
  const clientId: string | undefined = location.state?.clientId;
  const referralCode: string | undefined = location.state?.referralCode;

  useEffect(() => {
    if (!form.steps.isPersonalInfoPassed && isAppLoaded) {
      navigate(UI_ROUTES.firstEnroll, { replace: true });
    }
  }, [form.steps.isPersonalInfoPassed, isAppLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (requestState === ENROLL_REQUEST_STATE.READY) {
      // Push GTM event for client creation step 2
      const pushGtmEvent = async () => {
        const hashedEmail = await hashSHA256(form.email);
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.CLIENT_CREATION,
            event_step: GTM_CLIENT_CREATION_STEPS.STEP_2.step,
            event_category: GTM_CATEGORIES.CLIENT_CREATION_FUNNEL,
            event_action: GTM_CLIENT_CREATION_STEPS.STEP_2.action,
            event_label: GTM_CLIENT_CREATION_STEPS.STEP_2.label,
            form_name: GTM_FORM_NAMES.ENTER_SECURITY_DETAILS,
            client_id: userId,
            user_email_hashed: hashedEmail,
          },
        });
      };
      pushGtmEvent();

      EnrollmentState.setAuthInfoStepPassed(true);
      navigate(UI_ROUTES.emailConfirm);
    }
    if (requestState === ENROLL_REQUEST_STATE.ERROR) {
      setShowError(true);
    }
  }, [requestState, userId, form.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = useCallback((): boolean => {
    const countryCheck = validateControl(
      form.country,
      [],
      lang.commonOnboardingInitialCountryLabel(),
      true,
    );

    const firstNameCheck = getFirstNameValidity(form.firstName);
    const lastNameCheck = getLastNameValidity(form.lastName);
    const emailCheck = getEmailValidity(form.email);

    setFormErrors({
      ...formErrors,
      email: emailCheck.errors,
      firstName: firstNameCheck.errors,
      lastName: lastNameCheck.errors,
      country: countryCheck.errors,
      privacyPolicyError: !form.isPrivacyPolicyApproved
        ? lang.commonPolicyAgreementErrorMessage()
        : '',
    });

    return emailCheck.isValid && form.isPrivacyPolicyApproved;
  }, [form.email, form.isPrivacyPolicyApproved]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPasswordControl = useCallback((value: string): boolean => {
    const validationResult = validate(value, ValidationType.Password) as PasswordValidationResult;

    setPasswordValidationResult(validationResult);

    return (
      validationResult.specialChar
      && validationResult.maxCharacters
      && validationResult.number
      && validationResult.lowercaseLetter
      && validationResult.eightSymbols
      && validationResult.uppercaseLetter
      && validationResult.onlyLatinCharacters
    );
  }, []);

  const onSubmit = () => {
    setIsSubmitButtonClicked(true);
    setHasUserInteracted(true);
    if (validateForm()) {
      const extraDetails: ExtraUserDetails = {
        first_name: form.firstName,
        last_name: form.lastName,
        country_of_residence: form.country,
      };

      enrollRegister(
        form.email,
        form.password,
        form.password,
        extraDetails,
        planId,
        autoPass,
        clientId,
        referralCode,
      );
    }
  };

  useEffect(() => {
    if (hasUserInteracted) {
      const emailErrors = getEmailValidity(form.email).errors;

      // Calculate the total additional height for the policies
      const privacyPolicyError = !form.isPrivacyPolicyApproved ? 1 : 0;

      // Calculate total error fields including email and policies
      const totalErrorFields = (emailErrors.length > 0 ? 1 : 0) + privacyPolicyError;

      // Set the additional height based on the number of errors
      setAdditionalHeight(totalErrorFields * ADDITIONAL_HEIGHT_PER_ERROR);
    }
  }, [form.email, form.isPrivacyPolicyApproved, hasUserInteracted]);

  return (
    <Layout
      formTitle={lang.commonEnrollmentSecurityDetails()}
      onSubmit={onSubmit}
      displayFooter
      displayBackButton
      displayNextButton
      nextButtonText={lang.commonOnboardingSignInButtonCreateAccount()}
      displayLoginButton={false}
      additionalHeight={additionalHeight}
    >
      <>
        {showError && (
          <div className={styles.error}>
            <div className={styles.errorContent}>
              <img className={styles.errorIcon} src={InputErrorLogo} alt="error" />
              <div>
                <div>
                  {lang.commonErrorRegister1()}
                  <a href={`mailto:${config.links.aceHelpCenterUrl}`}>
                    <span className={styles.contact}>{lang.commonErrorRegister2()}</span>
                  </a>
                </div>
                {lang.commonErrorRegister3()}
              </div>
            </div>
          </div>
        )}
        <div
          className={classNames(styles.row, {
            [styles.errorRow]:
              isSubmitButtonClicked && getEmailValidity(form.email).errors.length > 0,
          })}
        >
          <span className={styles.title}>{lang.commonOnboardingSignInEmail()}</span>
          <InputControl
            name=""
            placeholder=""
            className={classNames(styles.input, styles.emailReg)}
            label={lang.commonEnrollmentEnterEmail()}
            value={form.email}
            onValueChange={(value) => {
              EnrollmentState.setEmail(String(value));
            }}
            errors={isSubmitButtonClicked ? getEmailValidity(form.email).errors : []}
            onSubmit={onSubmit}
          />
        </div>

        <div className={styles.row}>
          <span className={styles.titlePassword}>{lang.commonOnboardingSignInPassword()}</span>
          <PasswordControl
            name=""
            placeholder=""
            label={lang.commonEnrollmentEnterPassword()}
            visibleHint
            visibleAddon
            onSubmit={onSubmit}
            value={form.password}
            onValueChange={(value) => {
              EnrollmentState.setPassword(value);
              checkPasswordControl(value);
            }}
            validationFlags={passwordValidationResult}
            isSubmitButtonClicked={isSubmitButtonClicked}
          />
        </div>

        <div
          className={classNames(styles.row, {
            [styles.errorRow]: isSubmitButtonClicked && form.isPrivacyPolicyApproved,
          })}
        >
          <CheckboxControlV2
            id="privacyPolicyCheckbox"
            errorId="privacyPolicyCheckboxError"
            checked={form.isPrivacyPolicyApproved}
            centered={false}
            className={styles.checkbox}
            onChange={(value) => {
              EnrollmentState.setPrivacyPolicyApproved(value);
            }}
            component={(
              <p className={classNames(styles.text, { [styles.policyText]: !isMobile })}>
                {lang.commonEnrollmentApprovePrivacyPolicy1()}
                {' '}
                <Link
                  className={classNames(styles.policyButton, styles.policyLink, {
                    [styles.inlinePolicyButton]: !isMobile,
                  })}
                  target="_blank"
                  href={config.links.privacyPolicyUrl}
                >
                  {lang.commonEnrollmentApprovePrivacyPolicy3()}
                  {' '}
                  {lang.commonEnrollmentApprovePrivacyPolicy2()}
                  {' '}
                  {lang.commonEnrollmentApproveCookiesPolicy()}
                </Link>
              </p>
            )}
            error={!form.isPrivacyPolicyApproved ? formErrors.privacyPolicyError : undefined}
          />
        </div>
      </>
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  enrollRegister: (
    email: string,
    password: string,
    confirmPassword: string,
    extraDetails: ExtraUserDetails,
    planId: number,
    autoPass?: boolean,
    referrerClientId?: string,
    referralCode?: string,
  ) =>
    dispatch(
      enrollAuth({
        requestType: ENROLL_AUTH_REQUEST_TYPE.REGISTER,
        email,
        password,
        confirmPassword,
        extraDetails,
        planId,
        autoPass,
        referrerClientId,
        referralCode,
      }),
    ),
});

const mapStateToProps = (state: RootState) => ({
  requestState: state.auth.enroll.status,
  status: state.auth.enroll.status,
  isAppLoaded: state.app.loaded,
  userId: state.auth.enroll.userId,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollAuthInfo);
